import React from "react";
import { cn, getStatusConfig, getPriorityConfig } from "../../lib/utils";
import {
    CircleDashed,
    Circle,
    CircleDot,
    CheckCircle2,
    XCircle,
    Clock,
    AlertTriangle,
    ShieldCheck,
    Sparkles,
} from "lucide-react";

const statusIcons = {
    new: Sparkles,
    draft: CircleDashed,
    open: Circle,
    pending_approval: Clock,
    in_progress: CircleDot,
    resolved: CheckCircle2,
    closed: CheckCircle2,
    cancelled: XCircle,
};

const priorityIcons = {
    low: null,
    medium: null,
    high: AlertTriangle,
    critical: AlertTriangle,
};

export function StatusBadge({ status, className }) {
    const config = getStatusConfig(status);
    const Icon = statusIcons[status];
    return (
        <span
            className={cn("inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border", config.color, className)}
            data-testid={`status-badge-${status}`}
        >
            {Icon && <Icon className="w-3 h-3" />}
            {config.label}
        </span>
    );
}

export function PriorityBadge({ priority, className }) {
    const config = getPriorityConfig(priority);
    const Icon = priorityIcons[priority];
    return (
        <span
            className={cn("inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border", config.color, className)}
            data-testid={`priority-badge-${priority}`}
        >
            {Icon && <Icon className="w-3 h-3" />}
            {config.label}
        </span>
    );
}

/**
 * Calculate deadline urgency from ticket deadline_info
 */
export function getDeadlineUrgency(info) {
    if (!info || !info.has_deadline) return { state: "none", label: null };
    if (info.is_overdue) return { state: "overdue", label: info.remaining_label };
    if (info.remaining_seconds != null && info.remaining_seconds < 86400) {
        return { state: "warning", label: info.remaining_label };
    }
    return { state: "ok", label: info.remaining_label };
}

export function DeadlineBadge({ info, className }) {
    const urgency = getDeadlineUrgency(info);
    if (urgency.state === "none") return null;

    if (urgency.state === "overdue") {
        return (
            <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-300", className)} data-testid="deadline-badge-overdue">
                <AlertTriangle className="w-3 h-3" />{urgency.label}
            </span>
        );
    }
    if (urgency.state === "warning") {
        return (
            <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200", className)} data-testid="deadline-badge-warning">
                <Clock className="w-3 h-3" />{urgency.label}
            </span>
        );
    }
    return (
        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-600 border border-green-200", className)} data-testid="deadline-badge-ok">
            <ShieldCheck className="w-3 h-3" />{urgency.label}
        </span>
    );
}

// Legacy — kept for backward compat
export function getSlaUrgency(sla) {
    return getDeadlineUrgency(sla);
}
export function SLABadge({ sla, className }) {
    return <DeadlineBadge info={sla} className={className} />;
}
